'use client';

import React, { createContext, useContext, useEffect } from 'react';

interface ThemeContextType {
  /** CSS-color string ready for inline style props. Resolves to
   *  `var(--primary-color, <tenant-default>)` so EmbedShell's
   *  postMessage-based theme override flows through automatically:
   *  components read this string into `style={{ color: theme.primaryColor }}`,
   *  and the browser resolves the variable at paint time. The fallback
   *  baked into the string is the tenant's own default, so when there
   *  is NO embed override the inline color is identical to before.
   */
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  /** Contrast-safe foreground for content sitting ON a filled accent
   *  surface (CTAs, selected chips, radio checks). Resolves to the
   *  storefront-scoped `--sf-on-accent` var (set per-tenant in
   *  StorefrontPage from the accent's relative luminance), with a white
   *  fallback so behaviour is unchanged wherever the var is absent. */
  onAccent: string;
}

const FALLBACK_PRIMARY = '#ffffff';
const FALLBACK_SECONDARY = '#f3f4f6';
const FALLBACK_TEXT = '#000000';

const defaultTheme: ThemeContextType = {
  primaryColor: `var(--primary-color, ${FALLBACK_PRIMARY})`,
  secondaryColor: `var(--secondary-color, ${FALLBACK_SECONDARY})`,
  textColor: `var(--text-color, ${FALLBACK_TEXT})`,
  onAccent: `var(--sf-on-accent, #ffffff)`,
};

const ThemeContext = createContext<ThemeContextType>(defaultTheme);

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: React.ReactNode;
  theme?: {
    primary_color?: string;
    secondary_color?: string;
    text_color?: string;
  };
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, theme = {} }) => {
  // Resolved fallbacks for the CSS var() defaults — the tenant's own
  // colors are the static fallback when no embed-override is in play.
  const primaryFallback = theme.primary_color || FALLBACK_PRIMARY;
  const secondaryFallback = theme.secondary_color || FALLBACK_SECONDARY;
  const textFallback = theme.text_color || FALLBACK_TEXT;

  const themeValue: ThemeContextType = {
    primaryColor: `var(--primary-color, ${primaryFallback})`,
    secondaryColor: `var(--secondary-color, ${secondaryFallback})`,
    textColor: `var(--text-color, ${textFallback})`,
    onAccent: `var(--sf-on-accent, #ffffff)`,
  };

  // CSS-Variablen im :root setzen — Source-of-Truth für Theming.
  // EmbedShell überschreibt dieselben Vars wenn das Host-Page-Theme
  // Override sendet; ThemeProvider rendert sie initial vom Tenant-Default.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--primary-color', primaryFallback);
      document.documentElement.style.setProperty('--secondary-color', secondaryFallback);
      document.documentElement.style.setProperty('--text-color', textFallback);

      // Zusätzliche abgeleitete Farben
      const primaryRGB = hexToRGB(primaryFallback);
      if (primaryRGB) {
        document.documentElement.style.setProperty('--primary-rgb', primaryRGB);
      }
    }
  }, [primaryFallback, secondaryFallback, textFallback]);

  return <ThemeContext.Provider value={themeValue}>{children}</ThemeContext.Provider>;
};

// Hilfsfunktion zum Konvertieren von HEX zu RGB
function hexToRGB(hex: string): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : null;
}

export default ThemeProvider;
